import { KeyRound, Loader2, MailCheck } from "lucide-react"
import { useState, type FormEvent } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { authClient } from "@/lib/auth"
import { cn } from "@/lib/utils"

export type SignInMode = "sign-in" | "register" | "reset"

/** Supabase's messages are written for developers; these are for the person at the form. */
function friendly(message: string): string {
  const text = message.toLowerCase()
  if (text.includes("invalid login credentials")) return "That e-mail and password do not match an account."
  if (text.includes("email not confirmed")) {
    return "Your e-mail is not confirmed yet. Open the link in the message we sent you, then sign in."
  }
  if (text.includes("already registered")) return "There is already an account with that e-mail. Sign in instead."
  if (text.includes("password should be")) return "Choose a longer password - at least 8 characters."
  if (text.includes("rate limit") || text.includes("security purposes")) {
    return "Too many e-mails or attempts in a short time. Wait a minute and try again."
  }
  if (text.includes("fetch") || text.includes("network")) {
    return "Could not reach the sign-in service. Check your connection and try again."
  }
  return message
}

/** What the "check your e-mail" screen is waiting for. */
type Sent = { email: string; kind: "confirm" | "reset" }

/**
 * Sign in, register, or get back in after forgetting a password.
 *
 * Registering sends a confirmation e-mail; until the link in it is opened,
 * signing in is refused by Supabase itself. Both e-mails can be sent again
 * from here - a lost or expired link is the commonest reason someone cannot
 * get in. The tab is owned by the page, so the landing page's "Create a free
 * account" button can switch it from outside.
 *
 * No try/finally anywhere: Supabase returns failures - network ones included -
 * as an `error` value rather than throwing, and a try block would also stop
 * React Compiler from compiling this component (docs/weaknesses.md, W20).
 */
export function SignInCard({
  mode,
  onModeChange,
}: {
  mode: SignInMode
  onModeChange: (mode: SignInMode) => void
}) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unconfirmed, setUnconfirmed] = useState(false)
  const [sent, setSent] = useState<Sent | null>(null)
  const [resent, setResent] = useState(false)

  const redirect = window.location.origin

  async function submit(event: FormEvent) {
    event.preventDefault()
    const client = authClient()
    if (!client) return
    setBusy(true)
    setError(null)
    setUnconfirmed(false)
    if (mode === "sign-in") {
      const { error: failure } = await client.auth.signInWithPassword({ email, password })
      if (failure) {
        setError(friendly(failure.message))
        setUnconfirmed(failure.message.toLowerCase().includes("email not confirmed"))
      }
      // On success the gate hears about the new session and swaps this page out.
    } else if (mode === "register") {
      const { data, error: failure } = await client.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirect },
      })
      if (failure) setError(friendly(failure.message))
      else if (!data.session) setSent({ email, kind: "confirm" })
    } else {
      // Supabase answers the same whether or not the address has an account,
      // so this form cannot be used to find out who is registered.
      const { error: failure } = await client.auth.resetPasswordForEmail(email, {
        redirectTo: redirect,
      })
      if (failure) setError(friendly(failure.message))
      else setSent({ email, kind: "reset" })
    }
    setBusy(false)
  }

  async function sendAgain(target: Sent) {
    const client = authClient()
    if (!client) return
    setBusy(true)
    setError(null)
    const { error: failure } =
      target.kind === "confirm"
        ? await client.auth.resend({
            type: "signup",
            email: target.email,
            options: { emailRedirectTo: redirect },
          })
        : await client.auth.resetPasswordForEmail(target.email, { redirectTo: redirect })
    if (failure) setError(friendly(failure.message))
    else setResent(true)
    setBusy(false)
  }

  function switchTo(next: SignInMode) {
    onModeChange(next)
    setError(null)
    setUnconfirmed(false)
    setSent(null)
    setResent(false)
  }

  return (
    <div className="rounded-2xl border bg-card/80 p-6 shadow-xl shadow-black/5 backdrop-blur">
      {mode === "reset" ? (
        <div className="mb-6 space-y-1">
          <p className="flex items-center gap-2 font-medium">
            <KeyRound className="size-4 text-primary" aria-hidden />
            Reset your password
          </p>
          <p className="text-xs text-muted-foreground">
            We send a link to your e-mail. Open it on this device and choose a new password.
          </p>
        </div>
      ) : (
        <div className="mb-6 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1" role="tablist">
          {(["sign-in", "register"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={mode === tab}
              onClick={() => switchTo(tab)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm transition-colors",
                mode === tab ? "bg-background font-medium shadow-sm" : "text-muted-foreground",
              )}
            >
              {tab === "sign-in" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>
      )}

      {sent ? (
        <div className="space-y-3 text-center" role="status">
          <MailCheck className="mx-auto h-8 w-8 text-primary" aria-hidden />
          <p className="text-sm">
            {sent.kind === "confirm" ? "We sent a confirmation link to " : "If there is an account for "}
            <span className="font-medium">{sent.email}</span>
            {sent.kind === "confirm" ? "." : ", a reset link is on its way."}
          </p>
          <p className="text-xs text-muted-foreground">
            Open it on this device. Nothing after a few minutes? Check your spam folder, or send it
            again.
          </p>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy || resent}
              onClick={() => void sendAgain(sent)}
            >
              {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
              {resent ? "Sent again" : "Send it again"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => switchTo("sign-in")}>
              Back to sign in
            </Button>
          </div>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm text-muted-foreground">
              E-mail
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          {mode !== "reset" ? (
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <label htmlFor="password" className="text-sm text-muted-foreground">
                  Password
                </label>
                {mode === "sign-in" ? (
                  <button
                    type="button"
                    onClick={() => switchTo("reset")}
                    className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    Forgot your password?
                  </button>
                ) : null}
              </div>
              <Input
                id="password"
                type="password"
                autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
                required
                minLength={mode === "register" ? 8 : undefined}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {mode === "register" ? (
                <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              ) : null}
            </div>
          ) : null}

          {error ? (
            <div className="space-y-2" role="alert">
              <p className="text-sm text-destructive">{error}</p>
              {unconfirmed ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    const target: Sent = { email, kind: "confirm" }
                    setSent(target)
                    void sendAgain(target)
                  }}
                >
                  Send the confirmation e-mail again
                </Button>
              ) : null}
            </div>
          ) : null}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
            {mode === "sign-in" ? "Sign in" : mode === "register" ? "Create account" : "Send the reset link"}
          </Button>
          {mode === "register" ? (
            <p className="text-center text-xs text-muted-foreground">
              Free. We send one e-mail to confirm the address, and nothing else.
            </p>
          ) : null}
          {mode === "reset" ? (
            <button
              type="button"
              onClick={() => switchTo("sign-in")}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              Back to sign in
            </button>
          ) : null}
        </form>
      )}
    </div>
  )
}

/**
 * Shown after a password-reset link is opened: the link signs the user in
 * for exactly one purpose, choosing a new password, and the gate keeps every
 * other page out of reach until they have.
 */
export function NewPasswordCard({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    const client = authClient()
    if (!client) return
    setBusy(true)
    setError(null)
    const { error: failure } = await client.auth.updateUser({ password })
    setBusy(false)
    if (failure) setError(friendly(failure.message))
    else onDone()
  }

  return (
    <div className="rounded-2xl border bg-card/80 p-6 shadow-xl shadow-black/5 backdrop-blur">
      <div className="mb-6 space-y-1">
        <p className="flex items-center gap-2 font-medium">
          <KeyRound className="size-4 text-primary" aria-hidden />
          Choose a new password
        </p>
        <p className="text-xs text-muted-foreground">
          Your reset link worked. Set the password you will sign in with from now on.
        </p>
      </div>
      <form className="space-y-4" onSubmit={(event) => void submit(event)}>
        <div className="space-y-1.5">
          <label htmlFor="new-password" className="text-sm text-muted-foreground">
            New password
          </label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
          Save the new password
        </Button>
      </form>
    </div>
  )
}
